import "react-datepicker/dist/react-datepicker.css";

import { Modal } from "@waldner-laboreinrichtungen/component-system";
import type { Dispatch, FC, SetStateAction } from "react";
import { useEffect, useState } from "react";

import { exportChannelData } from "../api/lads";
import { useAccessToken } from "../auth-provider";
import { useActiveRoom } from "../hooks/use-rooms";
import type { Channel } from "../view-model";
import type { ExportStep } from "./channel-export-content";
import { ChannelExportContent } from "./channel-export-content";
import { ErrorScreenWithText } from "./error-screen";

export type ChannelExportProps = {
  channels: Channel[];
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
};

// Trigger a browser download for an already-fetched object URL (the XLSX blob
// the middleware streamed back on the no-USB / browser path).
const triggerDownload = (url: string, filename: string) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
};

const handleExport = async (
  token: string,
  setDownloadLink: (link: SetStateAction<string | null>) => void,
  setDownloadName: (name: string) => void,
  setStep: (step: ExportStep) => void,
  setError: (hasError: boolean) => void,
  collectionId: string,
  startDate: Date,
  endDate: Date,
  channels?: Channel[] | null
) => {
  setDownloadLink(null);
  setStep("loading");
  if (!channels) throw new Error("channels needed");

  const exportStartDate = new Date(startDate);
  exportStartDate.setHours(0, 0, 0, 0);

  const exportEndDate = new Date(endDate || startDate);
  exportEndDate.setHours(23, 59, 59, 999);

  const channelIds = channels.map((channel) => channel.id);
  try {
    const response = await exportChannelData(
      token,
      channelIds,
      collectionId,
      exportStartDate,
      exportEndDate
    );

    switch (response.status) {
      case 200: {
        // Browser download — the middleware streamed the XLSX directly.
        if (response.blob) {
          setDownloadName(response.filename ?? "export.xlsx");
          setDownloadLink(window.URL.createObjectURL(response.blob));
          setStep("ready");
        } else {
          setError(true);
        }
        break;
      }
      case 201: {
        setStep("ready-usb");
        break;
      }
      case 404: {
        setStep("no-usb-connected");
        break;
      }
      case 405: {
        setStep("no-usb-detected");
        break;
      }
      default: {
        setError(true);
      }
    }
  } catch {
    setError(true);
  }
};

export const ChannelExport: FC<ChannelExportProps> = ({
  channels,
  isOpen,
  setIsOpen,
}) => {
  const { activeRoom } = useActiveRoom();
  const exportCollectionId = activeRoom?.id.toString() || "";
  const token = useAccessToken();
  const [downloadLink, setDownloadLink] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState("export.xlsx");
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [step, setStep] = useState<ExportStep>("select");
  const [error, setError] = useState(false);

  useEffect(() => {
    if (error) {
      setStep("error");
    }
  }, [error]);

  const onChange = (dates: [Date, Date]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  if (!channels) {
    return <ErrorScreenWithText error={new Error("no channels found")} />;
  }

  const onClose = () => {
    setStep("select");
    setDownloadLink(null);
    setIsOpen(false);
    setError(false);
  };

  const onAccept = () => {
    handleExport(
      token!,
      setDownloadLink,
      setDownloadName,
      setStep,
      setError,
      exportCollectionId,
      startDate,
      endDate,
      channels
    );
  };

  return (
    <Modal onClose={onClose} open={isOpen} width="400px">
      <ChannelExportContent
        downloadLink={downloadLink}
        endDate={endDate}
        onAccept={onAccept}
        onChange={onChange}
        onClose={onClose}
        onDownload={(url) => triggerDownload(url, downloadName)}
        startDate={startDate}
        step={step}
      />
    </Modal>
  );
};
